import { useState } from 'react'
import { ChevDownIcon } from '../../icons'
import { cn } from '../../lib/cva'
import { MarkdownPreview } from '../MarkdownEditor'
import { Text } from '../Text'
import type { ComponentProps, DataViewerCustomProps, DataViewerResponseItem, DataViewerResult } from './types'
import { getLabelVariant } from './utils'

interface FormsDataViewerProps extends ComponentProps {
	data?: DataViewerResult[]
	loading?: boolean
	renderAsHtml?: boolean
}

const GRID_COLS = 'grid-cols-[minmax(110px,1.5fr)_minmax(80px,0.8fr)_minmax(180px,4fr)_minmax(90px,1fr)]'

function formatRoleLabel(role: string) {
	return role
		.split('_')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ')
}

function DataSourceHeader({ result }: { result: DataViewerResult }) {
	return (
		<div className='flex items-center justify-between gap-2 px-4 py-2.5 border-b border-neutral-200 bg-neutral-50'>
			<div className='min-w-0'>
				<Text variant='xsmall' weight='semibold' className='text-neutral-700 truncate'>
					{result.componentLabel}
				</Text>
				<Text variant='hint' className='text-neutral-400 truncate'>
					{result.cycleName}
				</Text>
			</div>
			<div className='shrink-0 px-2 py-0.5 rounded-full bg-primary-100'>
				<Text variant='hint' className='text-primary-700'>
					{result.responses.length} response{result.responses.length === 1 ? '' : 's'}
				</Text>
			</div>
		</div>
	)
}

function ResponseRow({
	response,
	striped,
	renderAsHtml,
}: Readonly<{
	response: DataViewerResponseItem
	striped: boolean
	renderAsHtml?: boolean
}>) {
	const dateLabel = response.submittedAt ? new Date(response.submittedAt).toLocaleDateString() : '—'

	return (
		<div
			className={cn(
				'grid items-start gap-x-3 px-4 py-3 border-b border-neutral-100 last:border-b-0',
				GRID_COLS,
				striped && 'bg-neutral-50/40',
			)}>
			<div className='min-w-0' title={response.evaluatorName}>
				<Text variant='xsmall' className='truncate'>
					{response.evaluatorName}
				</Text>
			</div>
			<div className='min-w-0' title={formatRoleLabel(response.role)}>
				<Text variant='xsmall' className='text-neutral-500 truncate'>
					{formatRoleLabel(response.role)}
				</Text>
			</div>
			<div className='min-w-0'>
				{renderAsHtml ? (
					<MarkdownPreview markdown={response.value} className='text-xs text-neutral-900 break-words' />
				) : (
					<Text variant='xsmall' className='whitespace-pre-wrap break-words'>
						{response.value}
					</Text>
				)}
			</div>
			<div className='min-w-0' title={dateLabel}>
				<Text variant='hint' className='text-neutral-400 truncate'>
					{dateLabel}
				</Text>
			</div>
		</div>
	)
}

function DataSourceTable({ result, renderAsHtml }: Readonly<{ result: DataViewerResult; renderAsHtml?: boolean }>) {
	return (
		<div className='w-full border border-neutral-200 rounded-md overflow-hidden'>
			<DataSourceHeader result={result} />
			<div className='overflow-x-auto'>
				<div className={cn('grid gap-x-3 px-4 border-b border-neutral-200 bg-neutral-50', GRID_COLS)}>
					{['Evaluator', 'Role', 'Response', 'Submitted'].map((label) => (
						<div key={label} className='py-2'>
							<Text variant='hint' className='text-neutral-500'>
								{label}
							</Text>
						</div>
					))}
				</div>
				{result.responses.map((response, idx) => (
					<ResponseRow
						key={`${result.componentId}-${idx}`}
						response={response}
						striped={idx % 2 === 1}
						renderAsHtml={renderAsHtml}
					/>
				))}
			</div>
		</div>
	)
}

function CollapsibleDataSource({
	result,
	defaultCollapsed,
	renderAsHtml,
}: Readonly<{
	result: DataViewerResult
	defaultCollapsed: boolean
	renderAsHtml?: boolean
}>) {
	const [isOpen, setIsOpen] = useState(!defaultCollapsed)

	return (
		<div className='flex flex-col gap-2'>
			<button
				type='button'
				className='flex items-start justify-between gap-2 text-left cursor-pointer group rounded-md border border-neutral-200 px-3 py-2.5 hover:bg-neutral-50'
				onClick={() => setIsOpen(!isOpen)}>
				<div className='min-w-0 flex items-start gap-2'>
					<ChevDownIcon
						size={14}
						className={cn(
							'mt-0.5 shrink-0 text-neutral-400 group-hover:text-neutral-600 transition-transform duration-200',
							!isOpen && '-rotate-90',
						)}
					/>
					<div className='min-w-0'>
						<Text variant='xsmall' weight='semibold' className='text-neutral-700 group-hover:text-neutral-900 truncate'>
							{result.componentLabel}
						</Text>
						<Text variant='hint' className='text-neutral-400 truncate'>
							{result.cycleName}
						</Text>
					</div>
				</div>
				<div className='shrink-0 mt-0.5 px-2 py-0.5 rounded-full bg-primary-100'>
					<Text variant='hint' className='text-primary-700'>
						{result.responses.length}
					</Text>
				</div>
			</button>
			{isOpen && <DataSourceTable result={result} renderAsHtml={renderAsHtml} />}
		</div>
	)
}

function LoadingPlaceholder() {
	return (
		<div className='w-full flex items-center justify-center py-8 border border-neutral-200 rounded-md bg-neutral-50/50'>
			<Text variant='xsmall' className='text-neutral-400'>
				Loading historical data…
			</Text>
		</div>
	)
}

export function FormsDataViewer({ component, data, loading, renderAsHtml }: Readonly<FormsDataViewerProps>) {
	const { label, labelColor, labelSize, labelWeight, collapsible, defaultCollapsed } =
		component.custom as DataViewerCustomProps

	const nonEmptyResults = data?.filter((d) => d.responses.length > 0) ?? []
	const hasData = nonEmptyResults.length > 0
	const isAdminPreview = !data && !loading

	// On the live client: hide the entire component when there's no data to show.
	// On the admin canvas (no data prop, not loading): show a placeholder so the component is visible.
	if (!loading && !hasData && !isAdminPreview) return null

	return (
		<div className='w-full flex flex-col gap-2 text-left'>
			{label && (
				<Text
					className={cn(labelWeight === 'hidden' && 'hidden')}
					variant={getLabelVariant(labelSize, 'small')}
					style={{ color: labelColor }}
					weight={(labelWeight !== 'hidden' && labelWeight) || null}>
					{label}
				</Text>
			)}

			{loading ? (
				<LoadingPlaceholder />
			) : hasData ? (
				<div className='flex flex-col gap-3'>
					{nonEmptyResults.map((result) =>
						collapsible ? (
							<CollapsibleDataSource
								key={`${result.cycleId}-${result.formId}-${result.componentId}`}
								result={result}
								defaultCollapsed={defaultCollapsed}
								renderAsHtml={renderAsHtml}
							/>
						) : (
							<DataSourceTable
								key={`${result.cycleId}-${result.componentId}`}
								result={result}
								renderAsHtml={renderAsHtml}
							/>
						),
					)}
				</div>
			) : (
				<div className='w-full flex items-center justify-center py-6 border border-dashed border-neutral-300 rounded-md bg-neutral-50/50'>
					<Text variant='xsmall' className='text-neutral-400'>
						Configure data sources in the properties panel
					</Text>
				</div>
			)}
		</div>
	)
}
